import classNames from 'classnames';
import { CSSProperties, ReactNode, useId } from 'react';
import styled, { css } from 'styled-components';

import { Horizontal } from 'atoms/layout/flex';

const commonStyles = css`
  position: relative;
  display: inline-flex;
  isolation: isolate;
  /* Otherwise the "filter: url(#round)" is very laggy on safari */
  will-change: filter;

  &:before {
    position: absolute;
    inset: 0;
    z-index: -1;
    content: '';
    display: block;

    .diamond & {
      clip-path: polygon(
        var(--offset) 0,
        calc(100% - var(--offset)) 0,
        100% 50%,
        calc(100% - var(--offset)) 100%,
        var(--offset) 100%,
        0% 50%
      );
    }

    .trapezoid & {
      clip-path: polygon(
        var(--offset) 0,
        calc(100% - var(--offset)) 0,
        100% 100%,
        0% 100%
      );
    }

    .inverted-trapezoid & {
      clip-path: polygon(
        0 0,
        100% 0,
        calc(100% - var(--offset)) 100%,
        var(--offset) 100%
      );
    }
  }
`;

const StyledSpan = styled.span`
  ${commonStyles}
  padding: var(--padding);
  --background-color: var(--bg);

  &:before {
    background: var(--background-color, var(--c-black));
  }

  &:hover {
    &:before {
      --background-color: var(--hover-bg);
    }
  }
`;

const StyledDiv = styled.div`
  ${commonStyles}
  padding: 1px 1.5px;

  &:before {
    background: var(--border);
  }
`;

type Props = {
  children: ReactNode;
  border?: string;
  background?: string;
  hoverBackground?: string;
  padding?: string;
  offset?: string;
  shape?: 'diamond' | 'trapezoid' | 'inverted-trapezoid';
};

export const EdgeRectangleShape = ({
  children,
  border,
  background,
  padding,
  offset = '15px',
  shape = 'diamond',
  hoverBackground = background,
}: Props) => {
  const id = useId();
  return (
    <Horizontal
      gap={0}
      style={
        {
          '--bg': background,
          '--hover-bg': hoverBackground,
          '--border': border,
          '--offset': offset,
        } as CSSProperties
      }
      className={classNames(shape)}
    >
      <svg
        style={{ visibility: 'hidden', position: 'absolute' }}
        width="0"
        height="0"
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
      >
        <defs>
          <filter id={id}>
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 10 -5"
              result="goo"
            />
            <feComposite in="SourceGraphic" in2="goo" operator="atop" />
          </filter>
        </defs>
      </svg>
      <StyledDiv style={{ filter: `url(#${id})` }}>
        <StyledSpan
          style={
            {
              '--padding': padding,
              filter: `url(#${id})`,
            } as CSSProperties
          }
        >
          {children}
        </StyledSpan>
      </StyledDiv>
    </Horizontal>
  );
};
